import type { Middleware } from '@reduxjs/toolkit';

import { logout } from '@store/actions/action-auth';
import type { RootState } from '@store/index';

/**
 * Redux middleware that intercepts all actions and checks for 401 errors
 * in the action payload. If found, automatically dispatches logout.
 *
 * This handles 401 errors from:
 * - Legacy Redux actions using superagent directly
 * - Any action that includes error information in the payload
 */
export const handle401ErrorsMiddleware: Middleware<object, RootState> =
  store => next => action => {
    const result = next(action);

    // Check if this action contains an error with a 401 status
    // Using 'any' here because Redux actions in this codebase have varying shapes
    const typedAction = action as any;

    // Check both modern (payload) and legacy (errorMsg, resp, error) patterns
    const { payload } = typedAction;
    const legacyError =
      typedAction.errorMsg || typedAction.resp || typedAction.error;
    const error = payload?.error || payload || legacyError;

    // Check various places where 401 might appear in error objects
    const is401 =
      error?.status === 401 ||
      error?.statusCode === 401 ||
      error?.response?.status === 401 ||
      error?.response?.statusCode === 401 ||
      error?.body?.statusCode === 401 ||
      // For legacy actions with error in different properties
      legacyError?.status === 401 ||
      legacyError?.statusCode === 401 ||
      legacyError?.response?.status === 401 ||
      // For actions that set error state directly
      (typedAction.type?.includes('ERROR') &&
        typeof payload === 'object' &&
        payload !== null &&
        (payload?.status === 401 ||
          payload?.statusCode === 401 ||
          payload?.data?.statusCode === 401));

    if (is401) {
      const state = store.getState();
      if (state.auth.isAuthenticated) {
        // Dispatch logout; server-side logout already happened (`401` from
        // server)
        store.dispatch(logout({ server: false }) as any);
      }
    }

    return result;
  };
